// src/components/membership/MembershipPlans-PopupDialog.js
// =============================================================================
// MembershipPlansPopupDialog
// - Confirmation dialog used when completing Oil Baron access by adding the missing BlackGold subscription.
// - Copy is derived from subscriptionStatus so the dialog explains what is already active and what will be added.
// =============================================================================

import React, { useEffect, useMemo } from "react";

import { PLAN_ID } from "./MembershipPlans-SubscriptionStatus.js";
import "./MembershipPlans-PopupDialog.css";

function planDisplayName(planId) {
    const id = (planId || "").toString();
    if (id === PLAN_ID.blackgoldRevenue) return "BlackGold – Revenue";
    if (id === PLAN_ID.blackgoldJib) return "BlackGold – JIB";
    if (id === PLAN_ID.oilBaron) return "Oil Baron";
    if (id === PLAN_ID.bigRig) return "Big Rig";
    if (id === PLAN_ID.wildcatter) return "Wildcatter";
    return id;
}

function resolveAddonContext(subscriptionStatus, request) {
    const hasRevenue = Boolean(subscriptionStatus?.hasRevenue);
    const hasJib = Boolean(subscriptionStatus?.hasJib);
    const hasExactlyOneBlackGold = (hasRevenue && !hasJib) || (hasJib && !hasRevenue);

    const existingPlanId = hasRevenue && !hasJib ? PLAN_ID.blackgoldRevenue : hasJib && !hasRevenue ? PLAN_ID.blackgoldJib : "";
    const computedMissingPlanId = hasExactlyOneBlackGold ? (hasRevenue ? PLAN_ID.blackgoldJib : PLAN_ID.blackgoldRevenue) : "";

    const requestedPlanId = (request?.requestedPlanId || "").toString();
    const targetPlanId = (request?.targetPlanId || computedMissingPlanId || "").toString();

    const isRequestedOilBaron = requestedPlanId === PLAN_ID.oilBaron;
    const isRequestedMissingPeer = requestedPlanId === computedMissingPlanId;

    return {
        hasExactlyOneBlackGold,
        hasRevenue,
        hasJib,
        existingPlanId,
        missingPlanId: computedMissingPlanId,
        targetPlanId,
        requestedPlanId,
        isRequestedOilBaron,
        isRequestedMissingPeer,
    };
}

export function MembershipPlansPopupDialog({ open, subscriptionStatus, request, onCancel, onProceed, text }) {
    const safeText = text || {};
    const ctx = useMemo(() => resolveAddonContext(subscriptionStatus, request), [subscriptionStatus, request]);

    const title = useMemo(() => {
        const fallback = safeText?.popupDialogTitle;
        if (!ctx?.hasExactlyOneBlackGold) return fallback;
        if (ctx?.isRequestedOilBaron) return safeText?.popupDialogTitleOilBaron || "Complete Oil Baron access";
        return fallback;
    }, [ctx?.hasExactlyOneBlackGold, ctx?.isRequestedOilBaron, safeText]);

    const bodyLines = useMemo(() => {
        const existing = planDisplayName(ctx?.existingPlanId);
        const adding = planDisplayName(ctx?.targetPlanId);
        const oilBaron = planDisplayName(PLAN_ID.oilBaron);

        if (!ctx?.hasExactlyOneBlackGold || !ctx?.existingPlanId || !ctx?.targetPlanId) {
            return [safeText?.popupDialogFallbackBody || "This will add an additional subscription."];
        }

        if (ctx?.isRequestedOilBaron) {
            return [
                safeText?.popupDialogOilBaronLine1 || `${existing} is already active.`,
                safeText?.popupDialogOilBaronLine2 || `Add ${adding} to unlock full ${oilBaron} access.`,
                safeText?.popupDialogOilBaronLine3 || "It will be billed separately.",
            ];
        }

        return [
            safeText?.popupDialogPeerLine1 || `${existing} is already active.`,
            safeText?.popupDialogPeerLine2 || `Add ${adding} to unlock full ${oilBaron} access.`,
            safeText?.popupDialogPeerLine3 || "It will be billed separately.",
        ];
    }, [ctx?.existingPlanId, ctx?.hasExactlyOneBlackGold, ctx?.isRequestedOilBaron, ctx?.targetPlanId, safeText]);

    const primaryLabel = useMemo(() => {
        if (safeText?.popupDialogPrimaryCta) return safeText.popupDialogPrimaryCta;
        const adding = planDisplayName(ctx?.targetPlanId);
        if (adding && adding !== ctx?.targetPlanId) return `Add ${adding}`;
        return "Proceed";
    }, [ctx?.targetPlanId, safeText]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") onCancel?.();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onCancel]);

    if (!open) return null;

    return (
        <div
            className="mc-addon-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={safeText?.popupDialogAriaLabel || "Subscription confirmation"}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onCancel?.();
            }}
        >
            <div className="mc-addon-modal">
                <div className="mc-addon-title">{title}</div>
                <div className="mc-addon-text">
                    {bodyLines.map((line, idx) => (
                        <div key={`${idx}-${line}`}>{line}</div>
                    ))}
                </div>
                <div className="mc-addon-actions">
                    <button type="button" className="mc-addon-btn danger" onClick={onCancel}>
                        {safeText?.popupDialogSecondaryCta || "Go Back"}
                    </button>
                    <button type="button" className="mc-addon-btn primary" onClick={onProceed}>
                        {primaryLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
